import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { Formik, Form, Field } from 'formik'
import classNames from 'classnames'
import { inject, observer } from 'mobx-react'
import * as Yup from 'yup'
import CountUp from 'react-countup'
import ConfettiExplosion from 'react-confetti-explosion'
import { FiX } from 'react-icons/fi'
import { Img } from 'react-image'

import { Button } from '/components/theme/button/Button'
import LoadingButton from '/components/common/LoadingButton'

import {
  VisbilityContainer,
  CommonPose
} from '/components/home/Animations'

const Results = inject('store')(observer(({ store, calculator, selected }) => {
  const { siteData } = store
  let savings = 0
  selected.forEach((s) => {
    s.forEach((p) => {
      savings += p.savings_per_user
    })
  })
  return (
    <ResultsWrapper>
      <ConfettiExplosion />
      <ResultTop>
        <ResultsTitle>
          <CountUp
            start={0}
            end={savings}
            duration={1}
            separator=","
            decimals={2}
            decimal="."
            prefix="$"
          />
        </ResultsTitle>
        <ResultsDescription>That's how much you could be saving as a member of <br /><strong>{siteData.get('name')}</strong></ResultsDescription>
      </ResultTop>
      { calculator.finish_text && (
        <ResultBottom>
          <p>{ calculator.finish_text }</p>
          <Button as="a" href={calculator.finish_url}>{ calculator.finish_cta_text }</Button>
        </ResultBottom>
      )}
    </ResultsWrapper>
  )
}))

const Progress = ({ stepNum, steps }) => {
  return (
    <ProgressWrapper>
      <ul className="progressbar-calc">
        {(steps || []).map((s, i) => (
          <li
            key={i}
            className={classNames({
              'active-current': stepNum === i,
              active: stepNum >= i
            })}
          ></li>
        ))}
      </ul>
    </ProgressWrapper>
  )
}

const WelcomeStep = ({ stepNum, step, setStep, add }) => {
  const handleSave = () => {
    setStep(stepNum + 1)
  }

  return (
    <StepWrapper>
      <TitleBlock>
        <Title>
          {step.title || 'Welcome to the calculator!'}
        </Title>
        { step.description && <Description>{step.description}</Description> }
      </TitleBlock>

      <Action className="text-center">
        <Button type="button" onClick={handleSave}>Continue</Button>
      </Action>
    </StepWrapper>
  )
}

const ProductSelectionStep = ({ stepNum, step, setStep, add }) => {
  const handleSave = (products) => {
    add(products)
    setStep(stepNum + 1)
  }
  const initialValues = {}

  return (
    <StepWrapper>
      <TitleBlock>
        <Title>
          {step.title || 'Pick what products you use or want to use'}
        </Title>
        { step.description && <Description>{step.description}</Description> }
      </TitleBlock>

      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors = {}
          const count = step.products.length
          if (count === 0 && step.produts.length > 1) {
            errors.global = 'Please select at least one option'
          }
          return errors
        }}
        onSubmit={async (values, { setSubmitting }) => {
          const selected = step.products.filter(
            (p) => p.id in values && values[p.id]
          )
          await handleSave(selected)
          setSubmitting(false)
        }}
      >
        {({ isValid, isSubmitting, setFieldValue, errors }) => (
          <FormWrapper>
            {errors.global && <Errors>{errors.global}</Errors>}
            <Categories>
              {step.products.map((c) => (
                <CategoryButton
                  key={c.id}
                  id={c.id}
                  label={c.product_name}
                  image={c.marketplace_image_url}
                  setFieldValue={setFieldValue}
                />
              ))}
            </Categories>
            <Action className="text-center">
              <LoadingButton
                text="Continue"
                isLoading={isSubmitting}
                disabled={!isValid}
              />
            </Action>
          </FormWrapper>
        )}
      </Formik>
    </StepWrapper>
  )
}

const YesNoStep = ({ stepNum, step, setStep, add }) => {
  const handleSave = (products) => {
    add(products)
    setStep(stepNum + 1)
  }

  return (
    <StepWrapper>
      <TitleBlock>
        <Title>
          {step.title || 'Are you interested in this?'}
        </Title>
        { step.description && <Description>{step.description}</Description> }
      </TitleBlock>

      <Formik
        initialValues={{ }}
        validationSchema={Yup.object({
          yes: Yup.bool().required()
        })}
        onSubmit={async (values, { setSubmitting }) => {
          const selected = values.yes ? step.products : []
          await handleSave(selected)
          setSubmitting(false)
        }}
      >
        {({ isValid, dirty, isSubmitting, setFieldValue, errors, values }) => (
          <FormWrapper>
            {errors.global && <Errors>{errors.global}</Errors>}
            <YesNoRow>
              <CommonPose>
                <Button hollow type="button" onClick={() => setFieldValue('yes', true)} active={values.yes === true} >
                  Yes
                </Button>
              </CommonPose>
              <CommonPose>
                <Button hollow type="button" onClick={() => setFieldValue('yes', false)} active={values.yes === false}>
                  No
                </Button>
              </CommonPose>
            </YesNoRow>
            <Action className="text-center">
              <LoadingButton
                text="Continue"
                isLoading={isSubmitting}
                disabled={!(isValid && dirty)}
              />
            </Action>
          </FormWrapper>
        )}
      </Formik>
    </StepWrapper>
  )
}

const MultiChoice = ({ stepNum, step, setStep, add }) => {
  const handleSave = (value) => {
    add([{ product_name: step.title, savings_per_user: value }])
    setStep(stepNum + 1)
  }

  const multi_choice_content = step?.multi_choice_content?.choices || []
  return (
    <StepWrapper>
      <TitleBlock>
        <Title>
          {step.title || 'Are you interested in this?'}
        </Title>
        { step.description && <Description>{step.description}</Description> }
      </TitleBlock>

      <Formik
        initialValues={{ }}
        validationSchema={Yup.object({
          selection: Yup.string().required()
        })}
        onSubmit={async (values, { setSubmitting }) => {
          await handleSave(values.selection)
          setSubmitting(false)
        }}
      >
        {({ isValid, dirty, isSubmitting, setFieldValue, errors, values }) => (
          <FormWrapper>
            {errors.global && <Errors>{errors.global}</Errors>}
            <YesNoRow>
              { multi_choice_content.map((c, i) => (
                <CommonPose key={i}>
                  <Button hollow type="button" onClick={() => setFieldValue('selection', c.value)} active={values.selection === c.value} >
                    {c.label}
                  </Button>
                </CommonPose>
              ))}
            </YesNoRow>
            <Action className="text-center">
              <LoadingButton
                text="Continue"
                isLoading={isSubmitting}
                disabled={!(isValid && dirty)}
              />
            </Action>
          </FormWrapper>
        )}
      </Formik>
    </StepWrapper>
  )
}

const ComponentPicker = ({ stepNum, step, setStep, add }) => {
  const params = { stepNum, step, setStep, add }
  switch (step.step_type) {
    case 'welcome':
      return (<WelcomeStep {...params } />)
    case 'product-selection':
      return (<ProductSelectionStep {...params } />)
    case 'yes-no':
      return (<YesNoStep {...params} />)
    case 'multi-choice':
      return (<MultiChoice {...params} />)
  }
}

const Calculator = inject('store')(
  observer(({ store, calculator }) => {
    const [stepNum, setStep] = useState(0)
    const [selected, setSelected] = useState([])
    const step = calculator?.steps[stepNum]
    const showResults = stepNum === calculator?.steps.length
    const add = (values) => {
      setSelected([...selected, values])
    }
    const { siteData } = store

    return (
      <Wrapper>
        <Header siteData={siteData} />
        <Content>
          <VisbilityContainer>
            {showResults ? (
              <Results calculator={calculator} selected={selected} />
            ) : (
              <div>
                <Progress stepNum={stepNum} steps={calculator?.steps} />
                <ComponentPicker key={stepNum} stepNum={stepNum} step={step} setStep={setStep} add={add} />
              </div>
            )}
          </VisbilityContainer>
        </Content>
        <Footer siteData={siteData} />
      </Wrapper>
    )
  }
))

export default Calculator

const CategoryButton = ({ id, label, image, setFieldValue }) => {
  const [checked, setChecked] = useState(false)
  const toggle = () => {
    const tmp = checked
    setFieldValue(id, !tmp, false)
    setChecked(!tmp)
  }
  return (
    <Category>
      <CatLabel htmlFor={id} active={checked}>
        {image && <CatImage src={image} />}
        {label}
      </CatLabel>
      <Field
        name={id}
        id={id}
        value={checked}
        type="checkbox"
        onClick={toggle}
      />
    </Category>
  )
}

const Action = styled(CommonPose)`
  padding: 50px 0;
  text-align: center;
`

const Categories = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 50px;
  flex-flow: wrap;
`

const Category = styled(CommonPose)`
  flex: 1 0 25%;
  margin: 0 10px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;

  input[type='checkbox'] {
    display: none;
  }
`

const CatLabel = styled.label`
  font-size: 1rem;
  font-weight: 400;
  padding: 20px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  border: #eee solid 2px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: all 0.25s;
  cursor: pointer;
  text-align: center;

  ${(props) =>
    props.active &&
    css`
      background-color: ${(props) => props.theme.colors.primary};
      color: #fff;
    `}

  &:hover {
    transform: scale(1.1);
    transition: all 0.25s;
  }
`

const CatImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
`

const Errors = styled.div`
  text-align: center;
  font-weight: bold;
  padding: 10px 0;
  color: ${(props) => props.theme.colors.danger};
  margin: 0 auto;
  font-weight: 400;
`

const YesNoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 20px;
`

const ProgressWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  padding: 20px;
`

const FormWrapper = styled(Form)`
  margin: 20px 0;
`

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const TitleBlock = styled.div`
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`

const Title = styled(CommonPose)`
  text-align: center;
  font-weight: 600;
  font-size: 1.5rem;
`

const Description = styled(CommonPose)`
  margin: 20px 0;
  text-align: center;
  font-size: 1.2rem;
`

const ResultsWrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const ResultTop = styled.div`
  flex: 1 0 auto;
`

const ResultBottom = styled.div`
  flex: 0 1 auto;
  text-align: center;
  width: 100%;
  border-top: 5px solid #eee;
  padding: 20px 0;
`

const ResultsTitle = styled(CommonPose)`
  text-align: center;
  font-weight: 800;
  font-size: 3rem;
  color: ${(props) => props.theme.colors.primary};
`

const ResultsDescription = styled(CommonPose)`
  margin: 20px 0;
  text-align: center;
  font-size: 1.2rem;
`

const Logo = styled(Img)`
  max-width: 200px;
  object-fit: contain;
`

const Header = ({ siteData }) => {
  const name = siteData.get('name')
  const branding = siteData.get('logo_media_center') ? (
    <Logo
      src={siteData.get('logo_media_center')?.media}
      alt={name}
    />
  ) : (
    <span>{name}</span>
  )

  return (
    <HeaderWrapper>
      { branding }
      <a href="/">
        <FiX size={30} />
      </a>
    </HeaderWrapper>
  )
}

const HeaderWrapper = styled.div`
  flex: 0 1 auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`

const Footer = ({ siteData }) => {
  const now = new Date()
  const year = now.getFullYear()
  const name = siteData.get('name')
  return (
    <FooterWrapper className="container">
      {name} © {year}. All rights reserved.
    </FooterWrapper>
  )
}

const FooterWrapper = styled.div`
  flex: 0 1 auto;
  font-size: 0.75rem !important;
  padding: 20px;
  text-align: center;
`

const Content = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
